import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { BlogPostDetail, BlogPostSummary } from '../seo/blog.config';
import { Lang } from '../seo/seo.config';

/**
 * Reads published articles from the SSR server's `/api/blog/*` proxy.
 *
 * On the server the fetch happens during rendering, so the full article is in the SSR HTML
 * crawlers read; `provideClientHydration` transfers the response, so the browser does not
 * fetch it again after hydration.
 *
 * Every failure resolves to an empty list / `null` rather than throwing: a blog outage must
 * degrade to "no articles", never to a 500 on a page a crawler is looking at.
 */
@Injectable({ providedIn: 'root' })
export class BlogService {
  private readonly lists = new Map<string, Observable<BlogPostSummary[]>>();
  private readonly posts = new Map<string, Observable<BlogPostDetail | null>>();

  constructor(private readonly http: HttpClient) {}

  list(lang: Lang): Observable<BlogPostSummary[]> {
    let request = this.lists.get(lang);
    if (!request) {
      request = this.http.get<BlogPostSummary[]>(`/api/blog/posts?lang=${lang}`).pipe(
        map((posts) => posts ?? []),
        catchError(() => of([])),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.lists.set(lang, request);
    }
    return request;
  }

  get(lang: Lang, slug: string): Observable<BlogPostDetail | null> {
    const key = `${lang}:${slug}`;
    let request = this.posts.get(key);
    if (!request) {
      request = this.http
        .get<BlogPostDetail>(`/api/blog/posts/${encodeURIComponent(slug)}?lang=${lang}`)
        .pipe(
          catchError(() => of(null)),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
      this.posts.set(key, request);
    }
    return request;
  }
}
